import { FormikProps, withFormik } from 'formik'
import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { useModal } from '@context/ModalsContext'

import { Button } from '@ui/components/Button'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { useGAEvents } from '@utils/callGAEvents'
import { useWindowResize } from '@utils/domUtils'

import { IBTestSubCategory } from '@model/TestSectionsDto'

import { SelectField } from '@global/field/SelectField'
import { createFormField } from '@global/formField/FormField'

import { useIBPrograms } from '@hooks/useIBPrograms'
import { useIBReferralCodes } from '@hooks/useIBReferralCodes'

import { Box } from '@shared/components/Box'
import { FieldWrapper } from '@shared/components/FieldWrapper'
import { InformationModal } from '@shared/modals/Information.modal'

import styles from './IBSignUpForm.module.scss'

export interface SignUpFormValues {
  selectedAnswers: string[]
  currency: string
}

interface OuterProps {
  subCategoryId?: IBTestSubCategory

  setSubCategoryId: Dispatch<SetStateAction<IBTestSubCategory | undefined>>
  handleSignUp: (selectedAnswers: string[]) => void
}

const FormField = createFormField<SignUpFormValues>()

const IBSignUpFormUI: React.FC<FormikProps<SignUpFormValues> & OuterProps> = (props) => {
  const { isValid, subCategoryId, setSubCategoryId, handleSubmit, setValues, values } = props

  const { t } = useTranslation()

  const { defaultProgram, multiTierProgram } = useIBPrograms()
  const { mtCode, ibCode, isLoading: isLoadingReferralCodes } = useIBReferralCodes()

  const { checkAccountIBAccess } = useAccountActionAccessCheck()

  const { callGAEvents } = useGAEvents()
  const currencyModal = useModal(InformationModal, {
    title: t('Currency'),
    description: t('IB.At the moment IB account is available only in USD.'),
  })

  const handleSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    checkAccountIBAccess(() => {
      handleSubmit()
    })
  }

  const handleProgramTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value as IBTestSubCategory

    if (newValue === IBTestSubCategory.Default) {
      callGAEvents().IBQuestionnaire.ProgramType.introducingBroker()
    } else if (newValue === IBTestSubCategory.MultiTier) {
      callGAEvents().IBQuestionnaire.ProgramType.multiTier()
    }

    setSubCategoryId(newValue)
  }

  const isMobile = useWindowResize()

  useEffect(() => {
    setValues({
      ...values,
      selectedAnswers: [],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryId])

  const programOptions = useMemo(
    () => [
      ...(defaultProgram && !ibCode
        ? [
            {
              value: IBTestSubCategory.Default,
              label: t('IB.Introducing Broker Program'),
            },
          ]
        : []),
      ...(multiTierProgram && !mtCode
        ? [
            {
              value: IBTestSubCategory.MultiTier,
              label: t('IB.Multi-tier Program'),
            },
          ]
        : []),
    ],
    [defaultProgram, ibCode, multiTierProgram, mtCode, t]
  )

  return (
    <Form className={styles.wrapper} onSubmit={handleSubmitForm}>
      <FieldWrapper>
        <SelectField
          value={subCategoryId}
          disabled={isLoadingReferralCodes || programOptions.length < 2}
          onChange={handleProgramTypeChange}
          trackEvent={() => callGAEvents().IBQuestionnaire.ProgramType.dropdownClick()}
          label={t('IB.IB Program')}
          options={programOptions}
        />
        <FormField
          name='currency'
          label={t('Currency')}
          rightIcon={InfoIcon}
          onEndIconClick={() => currencyModal.open()}
          disabled
          value={'USD'}
        />
      </FieldWrapper>
      {!!subCategoryId && (
        <Box flex justify='center'>
          <Box flex className={styles.submitButton}>
            <Button.Primary
              type='submit'
              size={isMobile ? 'md' : 'xl'}
              fullWidth
              disabled={!isValid}
            >
              {t('Submit')}
            </Button.Primary>
          </Box>
        </Box>
      )}
    </Form>
  )
}

export const IBSignUpForm = withFormik<OuterProps, SignUpFormValues>({
  mapPropsToValues: () => ({
    selectedAnswers: [],
    currency: 'USD',
  }),
  handleSubmit: ({ selectedAnswers }, { props }) => {
    props.handleSignUp(selectedAnswers)
  },
  enableReinitialize: true,
})(IBSignUpFormUI)
